/**
 * FROZEN ARTWORK → GRID placement discovery (#15).
 *
 * Bounded, viewport-aware: fetches only ACTIVE public placements intersecting the
 * currently visible world rect, and ONLY at the detail LOD (the GridViewer passes a
 * non-null `camera` past the LOD0 overview cutoff). Mirrors the publication-discovery
 * zero-stale-authority contract (monotonic generation + AbortController). Never loads
 * every Artwork in the universe; the backend spatial query is the authority.
 */
import { useEffect, useRef, useState } from "react";

import { artworkApi, type PublicPlacement } from "../lib/artwork-api";
import type { Camera } from "./camera";

const GRID = 1000;
const AREA_CAP = 250_000;

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** Visible world rect (integer, clamped, area-capped) from camera + viewport px. */
function worldViewport(camera: Camera): { x: number; y: number; w: number; h: number } | null {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const x0 = clamp(Math.floor(camera.x), 0, GRID);
  const y0 = clamp(Math.floor(camera.y), 0, GRID);
  let w = clamp(Math.ceil(vw / camera.scale) + 2, 1, GRID - x0);
  let h = clamp(Math.ceil(vh / camera.scale) + 2, 1, GRID - y0);
  if (x0 >= GRID || y0 >= GRID) return null;
  // Respect the backend area cap: shrink height first, then width if needed.
  if (w * h > AREA_CAP) {
    h = Math.max(1, Math.floor(AREA_CAP / w));
    if (w * h > AREA_CAP) w = Math.max(1, Math.floor(AREA_CAP / h));
  }
  return { x: x0, y: y0, w, h };
}

export function useArtworkPlacementDiscovery(camera: Camera | null, refreshKey = 0): PublicPlacement[] {
  const [items, setItems] = useState<PublicPlacement[]>([]);
  const genRef = useRef(0);
  const vp = camera ? worldViewport(camera) : null;
  const key = vp ? `${vp.x}:${vp.y}:${vp.w}:${vp.h}` : "";

  useEffect(() => {
    const myGen = ++genRef.current;
    if (!vp) {
      setItems([]);
      return;
    }
    const controller = new AbortController();
    artworkApi
      .publicPlacements(vp.x, vp.y, vp.w, vp.h, controller.signal)
      .then((r) => {
        if (myGen !== genRef.current) return;
        setItems(r.placements);
      })
      .catch(() => {
        if (myGen !== genRef.current || controller.signal.aborted) return;
        setItems([]);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, refreshKey]);

  return items;
}
